import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

interface Notification {
  id: number
  title: string
  message: string
  type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR'
  recipient: 'ALL' | 'ADMINS' | 'USERS' | 'SPECIFIC'
  status: 'SENT' | 'PENDING' | 'FAILED'
  created_at: string
  sent_at?: string
  read_count: number
  total_recipients: number
  metadata?: any
}

interface NotificationFilters {
  page?: number
  limit?: number
  search?: string
  type?: string
  status?: string
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
}

interface NotificationStats {
  total: number
  sent: number
  pending: number
  failed: number
  avgReadRate: number
}

interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

interface PaginatedApiResponse<T> extends ApiResponse<T[]> {
  meta?: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/notifications',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Notification', 'NotificationStats'],
  endpoints: (builder) => ({
    // Get paginated notifications with filters
    getNotifications: builder.query<PaginatedApiResponse<Notification>, NotificationFilters>({
      query: (filters) => {
        const params = new URLSearchParams()
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value))
          }
        })
        return `/?${params.toString()}`
      },
      transformResponse: (response: PaginatedApiResponse<Notification>) => response,
      providesTags: ['Notification'],
    }),

    // Get notification by ID
    getNotificationById: builder.query<Notification, number>({
      query: (id) => `/${id}`,
      transformResponse: (response: ApiResponse<Notification>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Notification', id }],
    }),

    // Get notification statistics
    getNotificationStats: builder.query<NotificationStats, void>({
      query: () => '/stats',
      transformResponse: (response: ApiResponse<NotificationStats>) => response.data,
      providesTags: ['NotificationStats'],
    }),

    // Create notification
    createNotification: builder.mutation<Notification, Partial<Notification>>({
      query: (notification) => ({
        url: '/',
        method: 'POST',
        body: notification,
      }),
      transformResponse: (response: ApiResponse<Notification>) => response.data,
      invalidatesTags: ['Notification', 'NotificationStats'],
    }),

    // Update notification status
    updateNotificationStatus: builder.mutation<
      void,
      { id: number; status: 'SENT' | 'PENDING' | 'FAILED'; readCount?: number; totalRecipients?: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/${id}/status`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Notification', 'NotificationStats'],
    }),

    // Delete notification
    deleteNotification: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Notification', 'NotificationStats'],
    }),
  }),
})

export const {
  useGetNotificationsQuery,
  useGetNotificationByIdQuery,
  useGetNotificationStatsQuery,
  useCreateNotificationMutation,
  useUpdateNotificationStatusMutation,
  useDeleteNotificationMutation,
} = notificationsApi






